/**
 * Session state for the GitLab login.
 *
 * `/api/auth/me` is the one endpoint that answers for anonymous callers, so it can be asked
 * before we know whether a login is needed at all. When SSO is switched off on the server it
 * reports `auth_enabled: false` and everything below renders exactly as it did before SSO
 * existed -- the gate is server-driven, not a build flag.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { request } from './client'

export interface AuthUser {
  subject: string
  name: string
  username: string | null
  email: string | null
  groups: string[]
  is_admin: boolean
}

export interface AuthState {
  auth_enabled: boolean
  configured: boolean
  provider: string
  authenticated: boolean
  /** True only on someone's very first signed-in page load; the server remembers it. */
  show_tutorial: boolean
  user: AuthUser | null
  login_url: string
}

export const authKey = ['auth', 'me'] as const

export function useAuth() {
  return useQuery({
    queryKey: authKey,
    queryFn: async (): Promise<AuthState> => {
      const response = await fetch('/api/auth/me')
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      return (await response.json()) as AuthState
    },
    // A cookie can expire while the tab is open; noticing on focus beats a wall of errors.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: false,
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await fetch('/api/auth/logout', { method: 'POST' })
    },
    onSuccess: () => {
      // Drop every cached answer: the next user of this browser must not see the last
      // one's board out of the cache.
      queryClient.clear()
      void queryClient.invalidateQueries({ queryKey: authKey })
    },
  })
}

/**
 * Note that the tutorial was shown, for this person on every device.
 *
 * Idempotent on the server, so the dialog may call it on each way out without checking
 * whether it already did -- and re-opening it from the header changes nothing.
 */
export function useMarkTutorialSeen() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => request<{ ok: boolean }>('/auth/tutorial-seen', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: authKey }),
  })
}

/** Start the flow, returning here afterwards. */
export function startLogin(loginUrl = '/api/auth/login'): void {
  const here = `${window.location.pathname}${window.location.search}`
  window.location.href = `${loginUrl}?next=${encodeURIComponent(here || '/')}`
}

/**
 * Whether this browser may use the administrator actions.
 *
 * With SSO off nobody is signed in and the whole board is shared, which is also how the
 * backend sees it (`auth.ANONYMOUS` is an admin) — so the actions are available.
 */
export function useIsAdmin(): boolean {
  const auth = useAuth()
  const state = auth.data
  if (!state) return false
  return state.auth_enabled ? Boolean(state.user?.is_admin) : true
}
